import React, {
    FC,
    useMemo,
    Dispatch,
    SetStateAction,
    ReactElement
} from 'react'
import { Pagination } from './Pagination'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { usePagination } from './Hooks/usePagination'
import { Tiles } from './Tiles'
import { ItemsCount } from './ItemsCount'
import { DropDownLimit } from './DropDownLimit'
import type {
    PaginationInterface,
    PaginationOptionsInterface
} from './Hooks/paginationHookTypes.d'
import { Button } from '@corratech/pylot-ui'
import cn from 'classnames'
import s from './PaginationToolbarStyle.module.scss'

const optionsArray = [
    {
        optionLabel: '10',
        optionValue: 10
    },
    {
        optionLabel: '20',
        optionValue: 20
    },
    {
        optionLabel: '50',
        optionValue: 50
    }
]

type Props = {
    className?: string
    options?: Array<PaginationOptionsInterface>
    showToolbar?: boolean
    pageControl: PaginationInterface
    setPageSize: Dispatch<SetStateAction<number>>
    prevIcon?: ReactElement | string
    nextIcon?: ReactElement | string
}

export const PaginationToolbar: FC<Props> = ({
    className,
    options = optionsArray,
    showToolbar = true,
    pageControl,
    setPageSize,
    prevIcon = <ChevronLeft size="20" />,
    nextIcon = <ChevronRight size="20" />
}) => {
    const {
        currentPage,
        setPage,
        totalPages,
        pageSize,
        totalCount
    } = pageControl

    const paginationOptions = usePagination(currentPage, setPage, totalPages)

    const {
        handleNavBack,
        handleNavForward,
        isActiveLeft,
        isActiveRight,
        tiles
    } = paginationOptions

    const navigationTiles = useMemo(
        () =>
            tiles.map((tileNumber) => {
                return (
                    <Tiles
                        isActive={tileNumber === currentPage}
                        key={tileNumber}
                        number={tileNumber}
                        onClick={setPage}
                    />
                )
            }),
        [currentPage, tiles, setPage]
    )

    if (!totalCount) return null

    return (
        <div className={cn(s['pagination-toolbar'], className)}>
            {showToolbar && <ItemsCount pageControl={pageControl} />}
            {totalPages > 1 && (
                <Pagination>
                    {isActiveLeft && (
                        <li className={cn(s['page-item'], s['previous'])}>
                            <Button
                                variant="link"
                                onClick={handleNavBack}
                                onKeyDown={handleNavBack}
                            >
                                <span aria-hidden="true">{prevIcon}</span>
                            </Button>
                        </li>
                    )}
                    {navigationTiles}
                    {isActiveRight && (
                        <li className={cn(s['page-item'], s['next'])}>
                            <Button
                                variant="link"
                                onClick={handleNavForward}
                                onKeyDown={handleNavForward}
                            >
                                <span aria-hidden="true">{nextIcon}</span>
                            </Button>
                        </li>
                    )}
                </Pagination>
            )}
            {showToolbar && (
                <DropDownLimit
                    setPageSize={setPageSize}
                    pageSize={pageSize}
                    setPage={setPage}
                    options={options}
                />
            )}
        </div>
    )
}
